'use strict';

import { debounce, isElement, forEach } from 'lodash';
import mustache from 'mustache';
import { isEarlier } from 'js-date-helper';

import { get as getPageOptions } from '../../01-helpers/utilities/options';
import { getElementNotFoundError } from '../../01-helpers/utilities/error';
import containerManager from '../../01-helpers/filter-context/container-manager';
import { publish, subscribe } from '../../01-helpers/event-manager';

import {
	TEETIMES_LOADED,
	TEETIMES_AVAILABLE,
	TEETIMES_UNAVAILABLE,
	TAB_ACTIVATED
} from '../../02-const/event-names';

import { fillItem, pushDataLayer } from '../../05-domain/enhanced-ecommerce/items';

/* ========================================================================== */

const moduleName = 'tt',
	selectors = {
		base: `.js-${moduleName}`,
		container: `.js-${moduleName}-results`,
		teetimeTemplate: '#teetimeTemplate'
	},
	labelKey = 'labels',
	itemKey = 'item';

/* ========================================================================== */

/**
 * 
 * @param {any} time
 * @param {any} fullHour
 */
function isTimeEarlier(time, date, fullHour = false) {
	const currentDate = new Date(),
		compareDateTime = new Date(`${currentDate.getMonth() + 1}/${currentDate.getDate()}/${currentDate.getFullYear()} ${currentDate.getHours()}:${fullHour ? '00' : currentDate.getMinutes()}`),
		teetimeDateTime = new Date(`${date} ${time}`);

	return isEarlier(teetimeDateTime, compareDateTime, false);
}

function renderTemplate(template, data) {
	mustache.tags = ['[[', ']]'];

	//check if data...time is earlier then right now.	
	if (isTimeEarlier(data.Time, data.date)) {
		return '';
	};

	const parsed = mustache.render(template, data);

	return parsed;
}

function renderTeeTimes(containerElement, teeTimes, teeTimeDate) {
	const template = this.baseElement?.querySelector(selectors.teetimeTemplate)?.innerHTML;

	containerElement.innerHTML = '';
	
	forEach(teeTimes, (teetime) => {		
		// compare teetime.time with currentTime
		if (isTimeEarlier(teetime.Time, teeTimeDate, true)) {
			// if teetime.time is before currentTime skip rendering
			return;
		}
				
		containerElement.innerHTML += `
			<div class="c-time">
				<span class="c-time__line"></span>
				<span class="c-time__title">${teetime.Time}</span>
			</div>
		`;

		forEach(teetime.TeeTimes, (item) => {
			item.date = teeTimeDate;
			containerElement.innerHTML += renderTemplate(template, item);
		});
	});
}

function onFilterContextUpdated() {
	this.filterContext.onUpdate(({ currentState }) => {
		if (!currentState.response) {
			return;
		}

		this.baseElement.querySelectorAll('[data-loop]').forEach((loopElement) => {
			const activeTab = this.baseElement.querySelector('input[name=loop]:checked');

			if (activeTab.id !== `loop-${loopElement.dataset.loop}`) {
				return;
			}

			const { response, date } = currentState,
				resultContainer = this.baseElement.querySelector(
					`#tab-${loopElement.dataset.loop}`
				);

			if (response.Error?.Code && response.Error?.Code !== 0) {
				publish(TEETIMES_UNAVAILABLE, loopElement);

				if (response.Error?.Text) {
					resultContainer.innerHTML = response.Error.Text;
				}

				// Break out of the loop, something went wrong
				return false;
			}

			if (response.TeeTimes.length > 0) {
				publish(TEETIMES_AVAILABLE, loopElement);

				renderTeeTimes.call(this, resultContainer, response.TeeTimes, date);
			} else {
				//TODO: make this text dynamic / translatable
				resultContainer.innerHTML = `
					<div>${this.labels.noTeeTimes}</div>
				`;

				publish(TEETIMES_UNAVAILABLE, loopElement);
			}
		});

		publish(TEETIMES_LOADED);
	});
}

/*==================================================================*/
function bindEvents() {
	subscribe(TAB_ACTIVATED, debounce(() => this._setCourseAndLoop(), 250));
}

export class TeeTimes {
	constructor(baseElement) {
		this._configure(baseElement);
		this._setup();
	}

	_configure(baseElement) {
		if (!isElement(baseElement)) {
			console.error(getElementNotFoundError(baseElement, Search));

			return false;
		}

		this.baseElement = baseElement;
		this.filterContext = containerManager.getContainer();
		this.labels = getPageOptions(labelKey);
		this.selectedItem = getPageOptions(itemKey);
	}

	_setup() {
		this._setCourseAndLoop();
		this._setDataLayer();
		bindEvents();
		onFilterContextUpdated.call(this);
	}

	/*==============================================*/

	_setCourseAndLoop() {
		// get coursenumber, and set the filtercontext for this.
		const loopsElement = this.baseElement.querySelector('[data-course]'),
			activeLoop = loopsElement.querySelector('input:checked'),
			activeTab = activeLoop.closest('.c-input__radio');

		if (isElement(loopsElement)) {
			this.filterContext.update(
				{
					coursenumber: loopsElement.dataset.course,
					loopnumber: activeTab.dataset.loop
				},
				{ refresh: true }
			);
		}
	}

	_setDataLayer() {
		const item = fillItem('Golfgo', this.selectedItem, { name: 'filter list', id: 'Fl1' }, 1);
		pushDataLayer('select_item', item);
	}
}
